import {
  useCreate,
  useGetIdentity,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useEffect, useId, useState, type FormEvent } from "react";

import { toIsoDay } from "@/components/market-intel/dates";
import {
  DateField,
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { amountInput, isDuplicateError } from "./forms";
import { PlatformBadge } from "./PlatformIcon";
import { PLATFORMS, platformLabelKey } from "./platforms";
import {
  normalizeProfileForm,
  normalizeSnapshotForm,
  profileFormDefaults,
  snapshotFormDefaults,
  type ProfileFormValues,
  type SnapshotFormValues,
} from "./social";
import type {
  CompetitorSocialProfile,
  CompetitorSocialSnapshot,
  SocialPlatform,
} from "./types";
import { FormActions } from "./ui";

// ---------- profile ----------

export const ProfileForm = ({
  open,
  onOpenChange,
  competitorId,
  profile,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competitorId: Identifier;
  /** Edits this profile; creates one when null. */
  profile: CompetitorSocialProfile | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const formId = useId();
  const [values, setValues] = useState(() => profileFormDefaults());
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate();
  const [update, { isPending: updating }] = useUpdate();

  useEffect(() => {
    if (open) {
      setValues(profileFormDefaults(profile ?? undefined));
      setError(null);
    }
  }, [open, profile]);

  const set =
    <K extends keyof ProfileFormValues>(key: K) =>
    (value: ProfileFormValues[K]) => {
      setValues((current) => ({ ...current, [key]: value }));
      setError(null);
    };
  const close = () => onOpenChange(false);
  const onFailure = (failure: unknown) =>
    notify(
      isDuplicateError(failure)
        ? "competition.social.duplicate"
        : "competition.form.error",
      { type: "error" },
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeProfileForm(competitorId, values);
    if (!result.ok) return setError(result.error);
    const options = {
      onSuccess: () => {
        notify("competition.form.saved", { type: "success" });
        close();
      },
      onError: onFailure,
    };
    if (profile)
      update(
        "competitor_social_profiles",
        { id: profile.id, data: result.data, previousData: profile },
        options,
      );
    else create("competitor_social_profiles", { data: result.data }, options);
  };

  const errorId = `${formId}-error`;
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        profile ? "competition.social.edit" : "competition.social.add",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <FormActions
          formId={formId}
          disabled={creating || updating}
          onCancel={close}
        />
      }
    >
      <Field
        id={`${formId}-platform`}
        label={translate("competition.social.form.platform")}
      >
        <select
          id={`${formId}-platform`}
          className={SELECT_CLASS}
          value={values.platform}
          onChange={(event) =>
            set("platform")(event.target.value as SocialPlatform)
          }
        >
          {PLATFORMS.map((platform) => (
            <option key={platform} value={platform}>
              {translate(platformLabelKey(platform))}
            </option>
          ))}
        </select>
      </Field>
      <Field
        id={`${formId}-handle`}
        label={translate("competition.social.form.handle")}
      >
        <Input
          id={`${formId}-handle`}
          value={values.handle}
          onChange={(event) => set("handle")(event.target.value)}
          placeholder="@cuenta"
          autoCapitalize="none"
          autoCorrect="off"
          aria-invalid={error ? true : undefined}
          aria-describedby={`${formId}-handle-hint${error ? ` ${errorId}` : ""}`}
          maxLength={140}
        />
        <p
          id={`${formId}-handle-hint`}
          className="text-xs text-muted-foreground"
        >
          {translate("competition.social.form.handle_hint")}
        </p>
      </Field>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      <Field
        id={`${formId}-url`}
        label={translate("competition.social.form.url")}
      >
        <Input
          id={`${formId}-url`}
          type="url"
          inputMode="url"
          value={values.url}
          onChange={(event) => set("url")(event.target.value)}
          aria-describedby={`${formId}-url-hint`}
        />
        <p id={`${formId}-url-hint`} className="text-xs text-muted-foreground">
          {translate("competition.social.form.url_hint")}
        </p>
      </Field>
      <Field
        id={`${formId}-notes`}
        label={translate("competition.social.form.notes")}
      >
        <Textarea
          id={`${formId}-notes`}
          value={values.notes}
          onChange={(event) => set("notes")(event.target.value)}
          rows={3}
        />
      </Field>
    </FormDialog>
  );
};

// ---------- snapshot ----------

const NUMBER_FIELDS = [
  ["followers", "numeric"],
  ["posts_count", "numeric"],
  ["following_count", "numeric"],
  ["posts_per_week", "decimal"],
  ["engagement_rate", "decimal"],
] as const;

export const SnapshotForm = ({
  open,
  onOpenChange,
  profile,
  history,
  snapshot,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: CompetitorSocialProfile | null;
  /** The profile's measurements: a new one on an existing day updates it. */
  history: readonly CompetitorSocialSnapshot[];
  /** Edits this measurement; adds one when absent. */
  snapshot?: CompetitorSocialSnapshot | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const { identity } = useGetIdentity();
  const formId = useId();
  const today = toIsoDay(new Date());
  const toInput = (value: number | null | undefined) =>
    amountInput(value, locale);
  const [values, setValues] = useState(() => snapshotFormDefaults(today));
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate();
  const [update, { isPending: updating }] = useUpdate();

  useEffect(() => {
    if (open) {
      setValues(snapshotFormDefaults(today, snapshot ?? undefined, toInput));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, snapshot]);

  if (!profile) return null;

  const set = (key: keyof SnapshotFormValues) => (value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setError(null);
  };
  const close = () => onOpenChange(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeSnapshotForm(profile.id, values, identity?.id);
    if (!result.ok) return setError(result.error);
    const sameDay =
      snapshot ??
      history.find((entry) => entry.measured_at === result.data.measured_at);
    if (
      snapshot &&
      history.some(
        (entry) =>
          entry.id !== snapshot.id &&
          entry.measured_at === result.data.measured_at,
      )
    )
      return setError("competition.social.snapshot_form.duplicate");
    const options = {
      onSuccess: () => {
        notify("competition.form.saved", { type: "success" });
        close();
      },
      onError: (failure: unknown) =>
        notify(
          isDuplicateError(failure)
            ? "competition.social.snapshot_form.duplicate"
            : "competition.form.error",
          { type: "error" },
        ),
    };
    if (sameDay) {
      // Keep the original author of an edited measurement.
      const { sales_id: _author, ...data } = result.data;
      update(
        "competitor_social_snapshots",
        { id: sameDay.id, data, previousData: sameDay },
        options,
      );
    } else
      create("competitor_social_snapshots", { data: result.data }, options);
  };

  const errorId = `${formId}-error`;
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        snapshot
          ? "competition.social.snapshot_form.edit_title"
          : "competition.social.snapshot_form.title",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <FormActions
          formId={formId}
          disabled={creating || updating}
          onCancel={close}
        />
      }
    >
      <div className="flex min-w-0 items-center gap-2 rounded-2xl bg-muted/50 px-3 py-2 dark:bg-white/5">
        <PlatformBadge platform={profile.platform} size="sm" showLabel />
        <span className="truncate text-sm font-semibold">{profile.handle}</span>
      </div>
      <Field
        id={`${formId}-measured_at`}
        label={translate("competition.social.snapshot_form.measured_at")}
      >
        <DateField
          id={`${formId}-measured_at`}
          value={values.measured_at}
          onChange={(event) => set("measured_at")(event.target.value)}
        />
      </Field>
      <div className="grid grid-cols-2 items-end gap-3 sm:grid-cols-3">
        {NUMBER_FIELDS.map(([key, inputMode]) => (
          <Field
            key={key}
            id={`${formId}-${key}`}
            label={translate(`competition.social.snapshot_form.${key}`)}
          >
            <Input
              id={`${formId}-${key}`}
              inputMode={inputMode}
              value={values[key]}
              onChange={(event) => set(key)(event.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
            />
          </Field>
        ))}
      </div>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      <Field
        id={`${formId}-source`}
        label={translate("competition.social.snapshot_form.source")}
      >
        <Input
          id={`${formId}-source`}
          value={values.source}
          onChange={(event) => set("source")(event.target.value)}
          aria-describedby={`${formId}-source-hint`}
        />
        <p
          id={`${formId}-source-hint`}
          className="text-xs text-muted-foreground"
        >
          {translate("competition.social.snapshot_form.source_hint")}
        </p>
      </Field>
      <Field
        id={`${formId}-notes`}
        label={translate("competition.social.snapshot_form.notes")}
      >
        <Textarea
          id={`${formId}-notes`}
          value={values.notes}
          onChange={(event) => set("notes")(event.target.value)}
          rows={2}
        />
      </Field>
    </FormDialog>
  );
};
